import Bowser from "bowser";

export interface BrowserData {
  name: string;
  version: string;
}

export interface OsData {
  name: string;
  version: string;
}

export interface DeviceData {
  type: string;
  vendor: string;
  model: string;
}

export interface DeviceInfo {
  browser: BrowserData;
  os: OsData;
  device: DeviceData;
}

export const getDeviceInfo = (userAgent?: string): DeviceInfo => {
  const ua = userAgent || (typeof navigator !== "undefined" ? navigator.userAgent : "");
  const result = Bowser.getParser(ua).getResult();

  return {
    browser: {
      name: result.browser.name || "",
      version: result.browser.version || ""
    },
    os: {
      name: result.os.name || "",
      version: result.os.version || ""
    },
    device: {
      type: result.platform.type || "",
      vendor: result.platform.vendor || "",
      model: result.platform.model || ""
    }
  };
};

const MINIMUM_BROWSER_VERSION_MAPPING: Record<string, number> = {
  Chrome: 103,
  Firefox: 102,
  Safari: 15
};

const MINIMUM_IOS_MAJOR_VERSION = 15;
const MINIMUM_IOS_MINOR_VERSION = 0;

export const supportedOperatingSystems = ["Android", "iOS"];

export const isIosVersionSufficient = (deviceInfo: DeviceInfo): boolean => {
  if (deviceInfo.os.name !== "iOS") {
    return false;
  }

  const versionParts = String(deviceInfo.os.version).split(".");
  const major = parseInt(versionParts[0], 10);
  const minor = parseInt(versionParts[1], 10) || 0;

  if (major > MINIMUM_IOS_MAJOR_VERSION) {
    return true;
  } else if (major === MINIMUM_IOS_MAJOR_VERSION) {
    return minor >= MINIMUM_IOS_MINOR_VERSION;
  }

  return false;
};

export const parseBrowserMajorVersion = (version: string | null): number => parseInt(String(version).split(".")[0], 10);

/**
 * Accepts any browser on a device running iOS 15.0 or later.
 * Otherwise it only accepts the browsers listed in the `getMinimumBrowserVersion`
 * config function
 */
export const isBrowserValid = (deviceInfo: DeviceInfo): boolean => {
  const minimumBrowserVersion = MINIMUM_BROWSER_VERSION_MAPPING[deviceInfo.browser.name];
  const parsedVersion = parseBrowserMajorVersion(deviceInfo.browser.version);
  const isBrowserVersionInsufficient =
    !minimumBrowserVersion || isNaN(parsedVersion) || parsedVersion < minimumBrowserVersion;

  if (isBrowserVersionInsufficient && !isIosVersionSufficient(deviceInfo)) {
    return false;
  } else {
    return true;
  }
};
